import { createContext, useContext } from 'react';

import {
  AriaFocusRingProps,
  FocusRingAria,
  useFocusRing as useReactAriaFocusRing,
} from '@react-aria/focus';
import { CSSObject, useTheme } from 'foundations-theming/utils';

export type FocusableParentContextType = {
  delegateFocus: boolean;
};

export const FocusableParentContext = createContext<FocusableParentContextType>(
  {
    delegateFocus: false,
  }
);

/**
 * Determines whether a focus ring should be shown on the focused element.
 * @example
 * const StyledButton = styled.button(({ $focusRingStyles }) => {...$focusRingStyles});
 *
 * const MyButton = () => {
 *   const {focusProps, focusRingStyles} = useTrellisFocusRing();
 *
 *   // Be sure to merge existing component props with `focusProps` as needed
 *   return <StyledButton {...focusProps} $focusRingStyles={focusRingStyles} />
 * };
 * @param props - Focus ring config.
 * @returns Focus ring state and styles to apply to the focused element. Spread the `focusProps` onto the element that will receive focus. Pass `focusRingStyles` to a Styled Component.
 */
export const useTrellisFocusRing = (
  props?: AriaFocusRingProps
): FocusRingAria & {
  /** Focus ring styles to apply to the focused element */
  focusRingStyles: CSSObject;
} => {
  const result = useReactAriaFocusRing(props);
  const theme = useTheme();
  const { delegateFocus } = useContext(FocusableParentContext);

  const condition = props?.isTextInput
    ? result.isFocused
    : result.isFocusVisible;

  const style = delegateFocus
    ? { outline: 'none' }
    : {
        outlineWidth: theme.borderWidth[200],
        outlineStyle: 'solid',
        outlineOffset: theme.space[25],
        outlineColor: theme.color['border-core-hover'],
      };

  return {
    ...result,
    focusRingStyles: condition ? style : {},
  };
};
